import { WebTokenType } from '@ps/platform-message-framework/webtokenserver/WebTokenType';
import type { ResolverTag } from '@ps/web-core-enums';
import { resolverTokenType, useDispatch } from '@ps/web-core-store';
import type { FetchTagPayload, ResolvedTag, RootState, Store } from '@ps/web-core-store';
import hash from 'object-hash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { BehaviorSubject, type Subscription } from 'rxjs';

import { useIsMounted } from '../useIsMounted/useIsMounted';

export type Options = Pick<FetchTagPayload, 'tokenType' | 'query' | 'brand' | 'site'>;

const cache: Record<string, BehaviorSubject<ResolvedTag>> = {};

export const useResolver = (tag?: ResolverTag | string, options: Options = {}): string => {
  const { select } = useStore() as Store;
  const brand = useSelector(select.cms.getBrandName);
  const site = useSelector(select.cms.getSiteName);
  const { resolver } = useDispatch();
  const [url, setUrl] = useState('');
  const isMounted = useIsMounted();

  const { query, tokenType } = options;
  const tagTokenType = tokenType || resolverTokenType[tag as keyof typeof resolverTokenType];
  const isCacheDisabled =
    tagTokenType && [WebTokenType.ICE, WebTokenType.RATIONAL_WEB_SERVICES].includes(tagTokenType);
  const optionQuery = JSON.stringify(query);
  const { getState } = useStore<RootState>();
  const cacheKey = hash({ tag, options, site, brand });

  const fetchResolverTag = useCallback(
    async (tag: ResolverTag | string, options: Options) => {
      await resolver.fetchTag({ tag, ...options });
      const resolvedTag = select.resolver.getTag(getState(), tag);
      return resolvedTag;
    },
    [getState, resolver, select.resolver]
  );

  const fetchCacheableResolverTag = useCallback(
    async (tag: ResolverTag | string, options: Options) => {
      if (isCacheDisabled) {
        const resolvedTag = await fetchResolverTag(tag, options);
        return new BehaviorSubject<ResolvedTag>(resolvedTag);
      }

      if (!cache[cacheKey]) {
        cache[cacheKey] = new BehaviorSubject<ResolvedTag>({ url: '' });
        const resolvedTag = await fetchResolverTag(tag, options);
        cache[cacheKey].next(resolvedTag);
      }

      return cache[cacheKey];
    },
    [cacheKey, isCacheDisabled, fetchResolverTag]
  );

  useEffect(() => {
    let tagSubscription: Subscription;
    if (tag && isMounted()) {
      fetchCacheableResolverTag(tag, {
        tokenType: options.tokenType,
        query: optionQuery ? (JSON.parse(optionQuery) as object) : {},
        brand: options.brand,
        site: options.site
      }).then((tagSubject) => {
        tagSubscription = tagSubject.subscribe((data) => {
          if (data?.url) {
            setUrl(data.url);
          }
        });
      });
    }

    return () => tagSubscription?.unsubscribe();
  }, [
    fetchCacheableResolverTag,
    tag,
    isMounted,
    options.tokenType,
    optionQuery,
    options.brand,
    options.site
  ]);

  return url;
};
